import { ClockIcon } from '@heroicons/react/20/solid';

import { AmountComparison, AmountDisplay } from '@/components/data-display/AmountDisplay';
import { CopyField } from '@/components/data-display/CopyField';
import { Countdown } from '@/components/data-display/Countdown';
import { env } from '@/config/env';
import { useCountdownDisplay } from '@/hooks/countdownDisplay';
import { cn } from '@/utils/cn';

export interface PaymentSummaryProps {
  amount: string;
  currency: string;
  decimalPlaces?: number | undefined;
  locale: string;
  amountLabel: string;
  receivedAmount?: string | null | undefined;
  requestedLabel: string;
  receivedLabel: string;
  publicId: string;
  referenceLabel: string;
  referenceHelp: string;
  copyLabel: string;
  copiedLabel: string;
  manualCopyLabel: string;
  countdownLabel: string;
  expiresAt: string | null;
  className?: string;
}

/**
 * Shared funnel chrome: amount (largest), copyable reference, presentational countdown.
 * Timer ticks live in `useCountdown` (I-9); this only displays them.
 */
export function PaymentSummary({
  amount,
  currency,
  decimalPlaces = 2,
  locale,
  amountLabel,
  receivedAmount,
  requestedLabel,
  receivedLabel,
  publicId,
  referenceLabel,
  referenceHelp,
  copyLabel,
  copiedLabel,
  manualCopyLabel,
  countdownLabel,
  expiresAt,
  className,
}: PaymentSummaryProps) {
  const { remainingMs } = useCountdownDisplay();
  const differs = receivedAmount !== null && receivedAmount !== undefined && receivedAmount !== amount;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {differs ? (
        <AmountComparison
          amount={amount}
          receivedAmount={receivedAmount}
          currency={currency}
          decimalPlaces={decimalPlaces}
          locale={locale}
          requestedLabel={requestedLabel}
          receivedLabel={receivedLabel}
        />
      ) : (
        <AmountDisplay
          amount={amount}
          currency={currency}
          decimalPlaces={decimalPlaces}
          locale={locale}
          size="xl"
          label={amountLabel}
        />
      )}
      {expiresAt !== null && (
        <div className="flex items-center gap-2">
          <ClockIcon aria-hidden="true" className="text-text-muted size-4 shrink-0" />
          <Countdown remainingMs={remainingMs} warnMs={env.sessionWarnSeconds * 1000} label={countdownLabel} />
        </div>
      )}
      <CopyField
        label={referenceLabel}
        value={publicId}
        copyLabel={copyLabel}
        copiedLabel={copiedLabel}
        manualCopyLabel={manualCopyLabel}
        monospace
      />
      <p className="text-text-muted text-xs leading-relaxed">{referenceHelp}</p>
    </div>
  );
}
